/* eslint-disable react-hooks/set-state-in-effect */
import React, { useEffect, useState, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import axios from "axios";
import { LogOut, RefreshCw, Mail, MessagesSquare, Sparkles, CalendarCheck, FileText, Download } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import Logo from "@/components/Logo";
import BlogManager from "@/pages/admin/BlogManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const TABS = [
  { key: "contacts", label: "Contact form", endpoint: "/admin/contacts", Icon: MessagesSquare },
  { key: "bookings", label: "Site survey bookings", endpoint: "/admin/bookings", Icon: CalendarCheck },
  { key: "resource_downloads", label: "Resource downloads", endpoint: "/admin/resources", Icon: Download },
  { key: "health_checks", label: "Health checks", endpoint: "/admin/health-checks", Icon: Sparkles },
  { key: "newsletter", label: "Newsletter", endpoint: "/admin/newsletter", Icon: Mail },
  { key: "blog_posts", label: "Blog posts", endpoint: null, Icon: FileText },
];

const StatPill = ({ label, value, active, onClick, Icon, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`text-left px-6 py-5 rounded-md border transition-all duration-200 flex items-start gap-4 ${
      active ? "bg-white border-ink shadow-[0_8px_24px_-12px_rgba(0,0,0,0.18)]" : "bg-white border-black/8 hover:border-black/25"
    }`}
  >
    <Icon className={`h-5 w-5 mt-1 ${active ? "text-accent" : "text-muted"}`} strokeWidth={1.6} />
    <div>
      <div className="font-mono text-[10px] tracking-overline uppercase text-muted">{label}</div>
      <div className="font-display font-bold text-[1.75rem] tracking-tightest leading-none mt-1">
        {value ?? "—"}
      </div>
    </div>
  </button>
);

const ContactsTable = ({ rows }) => (
  <table className="w-full text-[13px]">
    <thead>
      <tr className="text-left font-mono text-[10px] tracking-overline uppercase text-muted border-b border-black/8">
        <th className="py-3 pr-3">Date</th><th className="py-3 pr-3">Name</th><th className="py-3 pr-3">Email</th>
        <th className="py-3 pr-3">Company</th><th className="py-3 pr-3">Service</th><th className="py-3">Message</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.id} className="border-b border-black/5 hover:bg-paper-cool/60">
          <td className="py-3 pr-3 align-top whitespace-nowrap text-muted-foreground">{fmtDate(r.created_at)}</td>
          <td className="py-3 pr-3 align-top font-semibold text-ink">{r.first_name} {r.last_name}</td>
          <td className="py-3 pr-3 align-top"><a href={`mailto:${r.email}`} className="text-brand hover:underline">{r.email}</a>{r.phone && <div className="text-[12px] text-muted-foreground">{r.phone}</div>}</td>
          <td className="py-3 pr-3 align-top text-ink/80">{r.company || "—"}</td>
          <td className="py-3 pr-3 align-top text-ink/80">{r.service_interest || "—"}</td>
          <td className="py-3 align-top text-ink/80 whitespace-pre-wrap max-w-md">{r.message}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const HealthChecksTable = ({ rows }) => (
  <table className="w-full text-[13px]">
    <thead>
      <tr className="text-left font-mono text-[10px] tracking-overline uppercase text-muted border-b border-black/8">
        <th className="py-3 pr-3">Date</th><th className="py-3 pr-3">Name</th><th className="py-3 pr-3">Email</th>
        <th className="py-3 pr-3">Sites</th><th className="py-3 pr-3">Sector</th><th className="py-3 pr-3">Pain points</th><th className="py-3">Band</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.id} className="border-b border-black/5 hover:bg-paper-cool/60">
          <td className="py-3 pr-3 align-top whitespace-nowrap text-muted-foreground">{fmtDate(r.created_at)}</td>
          <td className="py-3 pr-3 align-top font-semibold text-ink">{r.first_name} {r.last_name}</td>
          <td className="py-3 pr-3 align-top"><a href={`mailto:${r.email}`} className="text-brand hover:underline">{r.email}</a></td>
          <td className="py-3 pr-3 align-top text-ink/80">{r.sites}</td>
          <td className="py-3 pr-3 align-top text-ink/80">{r.sector}</td>
          <td className="py-3 pr-3 align-top text-ink/80">{(r.pain_points || []).join(", ")}</td>
          <td className="py-3 align-top"><span className="inline-flex px-2 py-0.5 bg-accent/10 text-accent rounded-sm font-mono text-[10px] tracking-wider uppercase">{r.complexity}</span></td>
        </tr>
      ))}
    </tbody>
  </table>
);

const NewsletterTable = ({ rows }) => (
  <table className="w-full text-[13px]">
    <thead>
      <tr className="text-left font-mono text-[10px] tracking-overline uppercase text-muted border-b border-black/8">
        <th className="py-3 pr-3">Date</th><th className="py-3 pr-3">Email</th><th className="py-3">Source</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.id} className="border-b border-black/5 hover:bg-paper-cool/60">
          <td className="py-3 pr-3 align-top whitespace-nowrap text-muted-foreground">{fmtDate(r.created_at)}</td>
          <td className="py-3 pr-3 align-top"><a href={`mailto:${r.email}`} className="text-brand hover:underline">{r.email}</a></td>
          <td className="py-3 align-top text-ink/80 font-mono text-[12px]">{r.source}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const ResourceDownloadsTable = ({ rows }) => (
  <table className="w-full text-[13px]">
    <thead>
      <tr className="text-left font-mono text-[10px] tracking-overline uppercase text-muted border-b border-black/8">
        <th className="py-3 pl-4 pr-3">Date</th>
        <th className="py-3 pr-3">Resource</th>
        <th className="py-3 pr-3">Name</th>
        <th className="py-3 pr-3">Email</th>
        <th className="py-3 pr-3">Company</th>
        <th className="py-3 pr-3">Source</th>
        <th className="py-3 pr-4">UTM</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => {
        const utmParts = [r.utm_source, r.utm_medium, r.utm_campaign].filter(Boolean);
        const utmLabel = utmParts.length ? utmParts.join(" / ") : (r.referrer ? `ref: ${new URL(r.referrer).hostname}` : "—");
        return (
          <tr key={r.id} className="border-b border-black/5 hover:bg-paper-cool/60">
            <td className="py-3 pl-4 pr-3 align-top whitespace-nowrap text-muted-foreground">{fmtDate(r.created_at)}</td>
            <td className="py-3 pr-3 align-top font-semibold text-ink">{r.resource_title}</td>
            <td className="py-3 pr-3 align-top">{r.first_name} {r.last_name}</td>
            <td className="py-3 pr-3 align-top"><a href={`mailto:${r.email}`} className="text-brand hover:underline">{r.email}</a></td>
            <td className="py-3 pr-3 align-top text-ink/80">{r.company || "—"}</td>
            <td className="py-3 pr-3 align-top">
              <span className={`inline-block text-[10px] font-mono uppercase tracking-wide px-2 py-0.5 rounded-full ${r.source === "exit_intent" ? "bg-accent/10 text-accent" : "bg-brand/10 text-brand"}`}>
                {r.source === "exit_intent" ? "Exit-intent" : "Gate form"}
              </span>
            </td>
            <td className="py-3 pr-4 align-top text-ink/70 text-[12px] font-mono" title={r.referrer || ""}>{utmLabel}</td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("contacts");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hideTest, setHideTest] = useState(() => localStorage.getItem("cb_admin_hide_test") === "1");
  const qs = hideTest ? "?hide_test=true" : "";

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/stats${qs}`, { withCredentials: true });
      setStats(data);
    } catch (e) {
      // 401 is handled by AuthContext (redirect to login); log everything else.
      if (e?.response?.status !== 401) {
        console.error("[admin] fetchStats failed:", e);
      }
    }
  }, [qs]);

  const fetchRows = useCallback(async () => {
    const endpoint = TABS.find((t) => t.key === tab).endpoint;
    if (!endpoint) {
      // Tab manages its own data (e.g. blog_posts via BlogManager)
      setRows([]);
      setLoading(false);
      setError("");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}${endpoint}${qs}`, { withCredentials: true });
      setRows(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [tab, qs]);

  useEffect(() => {
    if (user) fetchStats();
  }, [user, fetchStats]);

  useEffect(() => {
    if (user) fetchRows();
  }, [tab, user, fetchRows]);

  if (user === null) {
    return <div className="min-h-screen flex items-center justify-center font-mono text-[12px] text-muted">Checking session…</div>;
  }
  if (user === false) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div className="min-h-screen bg-paper-cool">
      <header className="bg-white border-b border-black/8 sticky top-0 z-30">
        <div className="max-w-wide mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo size="md" />
            <span className="hidden md:inline-flex items-center px-2.5 py-1 bg-accent/10 text-accent rounded-sm font-mono text-[10px] tracking-overline uppercase">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="hidden md:inline-flex text-[13px] font-medium text-ink/70 hover:text-ink" data-testid="admin-view-site">
              View site →
            </Link>
            <button
              onClick={() => { fetchStats(); fetchRows(); }}
              className="inline-flex items-center gap-2 px-4 py-2 border border-black/15 text-ink text-[13px] font-semibold rounded-sm hover:border-ink transition-colors"
              data-testid="admin-refresh"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 px-4 py-2 border border-ink text-ink text-[13px] font-semibold rounded-sm hover:bg-ink hover:text-white transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-wide mx-auto px-6 md:px-10 py-10">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="font-mono text-[11px] tracking-overline uppercase text-accent mb-2">[ Dashboard ]</div>
            <h1 className="font-display font-bold text-[2rem] md:text-[2.5rem] tracking-tightest">Hi {user.name?.split(" ")[0] || "Admin"}.</h1>
          </div>
          <div className="flex items-center gap-4">
            <label className="inline-flex items-center gap-2 text-[13px] font-medium text-ink/70 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={hideTest}
                onChange={(e) => { setHideTest(e.target.checked); localStorage.setItem("cb_admin_hide_test", e.target.checked ? "1" : "0"); }}
                data-testid="admin-hide-test-toggle"
                className="h-4 w-4 accent-[#0048e3]"
              />
              Hide test entries
            </label>
            <p className="text-[13px] text-muted-foreground font-mono">
              {stats ? `Last refreshed ${new Date().toLocaleTimeString("en-GB")}` : "Loading…"}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10" data-testid="admin-tabs">
          {TABS.map((t) => (
            <StatPill
              key={t.key}
              label={t.label}
              value={stats?.[t.key]}
              active={tab === t.key}
              onClick={() => setTab(t.key)}
              Icon={t.Icon}
              testId={`admin-tab-${t.key}`}
            />
          ))}
        </div>

        <div className="bg-white border border-black/8 rounded-md p-2 overflow-x-auto" data-testid="admin-table">
          {tab === "blog_posts" && <BlogManager onChange={fetchStats} />}
          {tab !== "blog_posts" && (
            <>
              {loading && <div className="p-8 font-mono text-[12px] text-muted">Loading…</div>}
              {!loading && error && <div className="p-8 font-mono text-[12px] text-accent">{String(error)}</div>}
              {!loading && !error && rows.length === 0 && (
                <div className="p-12 text-center">
                  <div className="font-display font-semibold text-[1.2rem] mb-2">Nothing here yet.</div>
                  <div className="text-[13px] text-muted-foreground">As enquiries come in, they’ll appear right here.</div>
                </div>
              )}
              {!loading && !error && rows.length > 0 && (
                <div className="overflow-x-auto">
                  {tab === "contacts" && <ContactsTable rows={rows} />}
                  {tab === "bookings" && <ContactsTable rows={rows} />}
                  {tab === "resource_downloads" && <ResourceDownloadsTable rows={rows} />}
                  {tab === "health_checks" && <HealthChecksTable rows={rows} />}
                  {tab === "newsletter" && <NewsletterTable rows={rows} />}
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
